import { useAuthStore } from '@/stores/auth';
import axios, { AxiosRequestConfig } from 'axios';
import { useToast } from './use-toast';
import { isRef, Ref, ref, unref, watch } from 'vue';
import { useRouter } from 'vue-router';

export function useHttp<T extends any>(url?: string | Ref<string>) {
    const client = axios.create({
        baseURL: `${import.meta.env.VITE_API_URL}/api`,
        timeout: 60 * 4 * 1000,
    });
    client.interceptors.request.use((config) => {
        return new Promise((resolve) => {
            const auth = useAuthStore();
            if (!config.headers) config.headers = {};
            if (auth.token) {
                config.headers['Authorization'] = 'Bearer ' + auth.token;
            }
            return resolve(config);
        });
    });

    const processing = ref(false);
    const result = ref<T>();
    const errors = ref<Record<string, string>>({});

    async function exec(config?: string | AxiosRequestConfig) {
        let opts: AxiosRequestConfig = {};
        if (config) {
            opts = typeof config === 'string' ? { url: config, method: 'get' } : { ...config };
        } else {
            opts = url ? { url: unref(url), method: 'get' } : {};
        }
        try {
            processing.value = true;
            errors.value = {};
            const response = await client.request<T>(opts);
            result.value = response.data;
        } catch (error: any) {
            const toast = useToast();
            const auth = useAuthStore();
            const router = useRouter();
            if (error.response) {
                switch (error.response.status) {
                    case 401:
                        toast.error('Your session is inactive. Please login again.');
                        auth.signout();
                        router?.replace({ name: 'login', query: { continue: window.location.pathname } });
                        break;
                    case 422:
                        for (const err of error.response.data.errors) {
                            if (!errors.value[err.field]) errors.value[err.field] = err.message;
                        }
                        toast.error('Invalid data');
                        break;
                    case 500:
                        toast.error('Something went wrong');
                        break;
                    case 404:
                        toast.error('Resource not found');
                        break;
                    default:
                        toast.error(error.response.data?.error || error.response.data?.title || error.toString());
                }
            } else if (error.request) {
                console.error(error);
                toast.error('Could not connect to server.');
            } else {
                console.error(error);
                toast.error('Something went wrong.');
            }
            return Promise.reject(error);
        } finally {
            processing.value = false;
        }
        return result.value;
    }

    if (url) {
        if (isRef(url)) watch(url, exec);
        if (unref(url)) exec();
    }

    return {
        processing,
        result,
        errors,
        exec,
    };
}
